// Actions
import { ActionType } from "@ms/uno-actions/lib/local/ActionType";
// Constants
import { StoreEntityStatus } from "@ms/uno-constants/lib/local/StoreConstants";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import { logUnreachableAction } from "../../utilities/LoggingUtilities";
import { getStatusBasedOnResponse } from "../../utilities/StatusUtilities";
/**
 * Store for Teams data
 */ export class TeamsStore extends EventPublishingStore {
    get name() {
        return "TeamsStore";
    }
    getAllTeamsTasksTabsForUser() {
        const allTabs = [];
        if (this.teamsTasksTabsByTeam != null) {
            Object.keys(this.teamsTasksTabsByTeam).forEach((teamId)=>{
                const tabs = this.teamsTasksTabsByTeam?.[teamId] ?? [];
                allTabs.push(...tabs);
            });
        }
        return {
            data: allTabs,
            status: this.teamsTasksTabsByTeamStatus
        };
    }
    getTeamsTasksTabsForTeam(teamId) {
        return this.teamsTasksTabsByTeam?.[teamId] ?? null;
    }
    getTeamsTasksTabsForPlan(planId) {
        return this.teamsTasksTabsByPlan?.[planId] ?? null;
    }
    getChannel(channelId) {
        return this.channels[channelId] ?? null;
    }
    getTeamsMessageDetails(messageId) {
        return this.teamsMessageDetails[messageId] ?? null;
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.DeleteTeamsTasksTabSucceeded:
                storeChanged = this.handleDeleteTeamsTasksTabSucceededAction(action);
                break;
            case ActionType.FetchTeamsTasksTabsSucceeded:
                storeChanged = this.handleFetchTeamsTasksTabsSucceededAction(action);
                break;
            case ActionType.FetchTeamsTasksTabsFailed:
                storeChanged = this.handleFetchTeamsTasksTabsFailedAction(action);
                break;
            case ActionType.FetchTeamsTasksTabs:
                storeChanged = this.handleFetchTeamsTasksTabsAction(action);
                break;
            case ActionType.FetchChannelSucceeded:
                storeChanged = this.handleFetchChannelSucceededAction(action);
                break;
            case ActionType.FetchTeamsMessageDetailsSucceeded:
                storeChanged = this.handleFetchTeamsMessageDetailsSucceededAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    /**
     * Update the stored teams tasks tabs from the success response.
     * @param action The action fired for successfully fetching teams tasks tabs.
     */ handleFetchTeamsTasksTabsSucceededAction(action) {
        let storeChanged = false;
        this.teamsTasksTabsByTeam = {};
        action.tabs.forEach((tab)=>{
            // If the store is empty, then initialize it.
            if (this.teamsTasksTabsByTeam == null) {
                this.teamsTasksTabsByTeam = {};
            }
            if (this.teamsTasksTabsByPlan == null) {
                this.teamsTasksTabsByPlan = {};
            }
            // If the team is not in the store, then add it.
            if (this.teamsTasksTabsByTeam[tab.groupIdTeamId] == null) {
                this.teamsTasksTabsByTeam[tab.groupIdTeamId] = [];
                this.teamsTasksTabsByTeam[tab.groupIdTeamId].push(tab);
            } else {
                // If the team is in the store, then add the tab if it is not already there.
                const existingTabIndex = this.teamsTasksTabsByTeam[tab.groupIdTeamId].findIndex((t)=>t.id === tab.id);
                if (existingTabIndex === -1) {
                    this.teamsTasksTabsByTeam[tab.groupIdTeamId].push(tab);
                } else if (this.teamsTasksTabsByTeam[tab.groupIdTeamId][existingTabIndex] !== tab) {
                    this.teamsTasksTabsByTeam[tab.groupIdTeamId][existingTabIndex] = tab;
                }
            }
            // If the plan is not in the store, then add it.
            if (this.teamsTasksTabsByPlan[tab.entityId] == null) {
                this.teamsTasksTabsByPlan[tab.entityId] = [];
                this.teamsTasksTabsByPlan[tab.entityId].push(tab);
            } else {
                // If the plan is in the store, then add the tab if it is not already there.
                const existingTabIndex = this.teamsTasksTabsByPlan[tab.entityId].findIndex((t)=>t.id === tab.id);
                if (existingTabIndex === -1) {
                    this.teamsTasksTabsByPlan[tab.entityId].push(tab);
                } else if (this.teamsTasksTabsByPlan[tab.entityId][existingTabIndex] !== tab) {
                    this.teamsTasksTabsByPlan[tab.entityId][existingTabIndex] = tab;
                }
            }
            storeChanged = true;
        });
        if (this.teamsTasksTabsByTeamStatus !== StoreEntityStatus.Present) {
            this.teamsTasksTabsByTeamStatus = StoreEntityStatus.Present;
            storeChanged = true;
        }
        return storeChanged;
    }
    /**
     * Update the stored teams tasks tabs from the failure response.
     * @param action The action fired for failing to fetch teams tasks tabs.
     */ handleFetchTeamsTasksTabsFailedAction(action) {
        let storeChanged = false;
        const { response } = action;
        const status = response != null ? getStatusBasedOnResponse(response) ?? StoreEntityStatus.UnableToFetch : StoreEntityStatus.UnableToFetch;
        if (status != null && this.teamsTasksTabsByTeamStatus !== status) {
            this.teamsTasksTabsByTeamStatus = status;
            storeChanged = true;
        }
        return storeChanged;
    }
    /**
     * Update the stored teams tasks tabs status from the action.
     * @param action The action fired for initiating fetch teams tasks tabs.
     */ handleFetchTeamsTasksTabsAction(action) {
        let storeChanged = false;
        if (this.teamsTasksTabsByTeamStatus !== StoreEntityStatus.Fetching) {
            this.teamsTasksTabsByTeamStatus = StoreEntityStatus.Fetching;
            storeChanged = true;
        }
        return storeChanged;
    }
    /**
     * Update the stored channel from the response.
     * @param action The action fired for successfully fetching channel.
     */ handleFetchChannelSucceededAction(action) {
        const channel = action.channel;
        if (this.channels[channel.id] !== channel) {
            this.channels[channel.id] = channel;
            return true;
        }
        return false;
    }
    /**
     * Update the stored teams message details from the response.
     * @param action The action fired for successfully fetching teams chat/channel message details.
     */ handleFetchTeamsMessageDetailsSucceededAction(action) {
        if (this.teamsMessageDetails[action.messageDetails.id] !== action.messageDetails) {
            this.teamsMessageDetails[action.messageDetails.id] = action.messageDetails;
            return true;
        }
        return false;
    }
    /**
     * Update the stored teams tasks tabs from the deletion success response.
     * @param action The action fired for successfully deleting a teams tasks tab.
     */ handleDeleteTeamsTasksTabSucceededAction(action) {
        const { groupId, channelId, id, planId } = action;
        let storeChanged = false;
        if (this.teamsTasksTabsByTeam != null) {
            const tabs = this.teamsTasksTabsByTeam[groupId];
            if (tabs != null) {
                const tabIndex = tabs.findIndex((tab)=>tab.id === id && tab.channelId === channelId);
                if (tabIndex !== -1) {
                    tabs.splice(tabIndex, 1);
                    storeChanged = true;
                }
            }
        }
        if (this.teamsTasksTabsByPlan != null) {
            const tabs = this.teamsTasksTabsByPlan[planId];
            if (tabs != null) {
                const tabIndex = tabs.findIndex((tab)=>tab.id === id && tab.channelId === channelId);
                if (tabIndex !== -1) {
                    tabs.splice(tabIndex, 1);
                    storeChanged = true;
                }
            }
        }
        return storeChanged;
    }
    constructor(loggers){
        super(), this.loggers = loggers, this.teamsTasksTabsByTeamStatus = StoreEntityStatus.Unfetched;
        this.teamsTasksTabsByTeam = null;
        this.teamsTasksTabsByPlan = null;
        this.channels = {};
        this.teamsMessageDetails = {};
    }
}
